import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {Lift} from '@models/lift.model';
import {select, Store} from '@ngrx/store';
import * as LiftActions from '@app/lift/store/lifts.actions';
import {combineLatest, Observable} from 'rxjs';
import * as LiftReducer from '@app/lift/store/lifts.reducer';
import {debounceTime, distinctUntilChanged, filter, map} from 'rxjs/operators';
import {UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {Subscription} from 'rxjs';
import {InputComponent, InputNumberComponent, SwitchComponent} from '@components/form';
import {Tray} from '@models/tray.model';
import {ToolModalComponent} from '@app/lift/components/tool-modal/tool-modal.component';
import {Slot} from '@models/slot.model';
import {Group} from '@app/tool/model/group.model';
import {ConfirmationModalComponent} from "@app/shared/components";
import {TrayType} from "@models/enums/tray-type.enum";
import {ToastsManager} from "@app/shared/util/toastr.service";
import {Line} from '@models/line.model';
import {LiftToolFilters} from '../../models/lift-tool-filters.model';
import {MenuItem} from 'primeng/api';
import {ContextMenuComponent} from '@app/shared/components/context-menu/context-menu.component';
import {TranslateService} from '@ngx-translate/core';

@Component({
    selector: 'app-lift-details',
    templateUrl: './lift-details.component.html',
    styleUrls: ['./lift-details.component.scss']
})
export class LiftDetailsComponent implements OnInit, OnDestroy {

    @ViewChild('trayName', {static: false}) trayName: InputNumberComponent;
    @ViewChild('trayPosition', {static: false}) trayPositions: InputNumberComponent;
    @ViewChild(SwitchComponent, {static: false}) forService: InputComponent;


    @ViewChild(ToolModalComponent, {static: false}) modal: ToolModalComponent;
    @ViewChild('removeTray', {static: false}) confirmRemoveTray: ConfirmationModalComponent;
    @ViewChild('removeLift', {static: false}) confirmRemoveLift: ConfirmationModalComponent;
    @ViewChild('changeVisibilityTray', {static: false}) confirmChangeVisibilityTray: ConfirmationModalComponent;
    @ViewChild('trayContextMenu', {static: true}) trayContextMenu: ContextMenuComponent;

    private _currentLiftId;

    isLoading$: Observable<boolean>;
    isLoadingLift$: Observable<boolean>;
    selectedLift$: Observable<Lift>;

    deletedLift$: Observable<Lift>;
    deletedTray$: Observable<Tray>;
    tools$: Observable<Group[]>;
    isLoadingTools$: Observable<boolean>;

    selectedLiftSubscription: Subscription;
    deleteTraySubscription: Subscription;
    deleteLiftSubscription: Subscription;
    changeVisibilitySubscription: Subscription;
    private routeSubscription: Subscription;
    private filterSubscription: Subscription;
    private linesSubscription: Subscription;

    selectedLift: Lift;
    toBeDeletedTray: Tray;
    toChangeVisibilityTray: Tray;

    liftForm: UntypedFormGroup;
    trayForm: UntypedFormGroup;
    tools: any[];
    lines$: Observable<Line[]>;
    lines: Line[] = [];
    activePanel: 'filters' | 'add' | 'edit' | null = 'filters';
    trayContextMenuItems: MenuItem[] = [];
    filterForm = new UntypedFormGroup({
        toolUID: new UntypedFormControl(null),
        toolName: new UntypedFormControl(null),
        line: new UntypedFormControl(null)
    });


    constructor(private route: ActivatedRoute,
                private router: Router,
                private translate: TranslateService,
                private toastr: ToastsManager,
                private store: Store<any>) {

        this.isLoading$ = this.store.pipe(select(LiftReducer.isLoading));
        this.isLoadingLift$ = this.store.pipe(select(LiftReducer.isLoadingLift));
        this.selectedLift$ = this.store.pipe(select(LiftReducer.getLift), filter(lift => !!lift))
        this.tools$ = this.store.pipe(select(LiftReducer.getTools), filter(tools => tools !== null));
        this.isLoadingTools$ = this.store.pipe(select(LiftReducer.isLoadingTools));


        this.selectedLiftSubscription = this.selectedLift$.subscribe((lift: Lift) => {
            this.selectedLift = lift;
            this.liftForm = new UntypedFormGroup({
                name: new UntypedFormControl(lift.name, Validators.required),
            })
            if (this.activePanel === 'edit') {
                this.activePanel = null;
            }
        });

        this.trayForm = new UntypedFormGroup({
            name: new UntypedFormControl(null, Validators.required),
            slots: new UntypedFormControl(null, Validators.required),
            forService: new UntypedFormControl()
        });

        this.deletedLift$ = this.store.pipe(select(LiftReducer.getDeletedLift), filter(lift => !!lift));
        this.deletedTray$ = this.store.pipe(select(LiftReducer.getDeletedTray), filter(lift => !!lift));

        this.lines$ = this.store.pipe(select(LiftReducer.getLines), filter(lines => !!lines));
        this.linesSubscription = this.lines$.subscribe(lines => {
            this.lines = lines;
            const selectedLine = this.filterForm.value.line;
            const line = selectedLine && lines.find(item => item.id === selectedLine.id);
            if (line) {
                this.filterForm.patchValue({line}, {emitEvent: false});
            }
        });

        this.routeSubscription = combineLatest([this.route.paramMap, this.route.queryParamMap]).pipe(
            map(([params, query]) => ({
                id: +params.get('id'),
                toolUID: query.get('toolUID') || undefined,
                toolName: query.get('toolName') || undefined,
                lineId: Number(query.get('lineId')) || undefined
            })),
            distinctUntilChanged((previous, current) => previous.id === current.id
                && previous.toolUID === current.toolUID && previous.toolName === current.toolName
                && previous.lineId === current.lineId)
        ).subscribe(filters => {
            this._currentLiftId = filters.id;
            const currentLine = this.filterForm.value.line;
            const line = this.lines.find(item => item.id === filters.lineId)
                || (filters.lineId ? {id: filters.lineId, name: ''} : currentLine?.id == null ? currentLine : null);
            this.filterForm.patchValue({
                toolUID: filters.toolUID || null,
                toolName: filters.toolName || null,
                line
            }, {emitEvent: false});
            this.getLift(this._currentLiftId);
        });

        this.filterSubscription = this.filterForm.valueChanges.pipe(debounceTime(300)).subscribe(() => {
            const filters = this.getToolFilters();
            this.router.navigate([], {
                relativeTo: this.route,
                queryParams: {
                    toolUID: filters.toolUID || null,
                    toolName: filters.toolName || null,
                    lineId: filters.lineId || null
                },
                queryParamsHandling: 'merge',
                replaceUrl: true
            });
        });

        this.store.dispatch(new LiftActions.LoadLinesAction());
    }

    ngOnInit() {
    }

    ngOnDestroy(): void {
        this.routeSubscription?.unsubscribe();
        this.filterSubscription?.unsubscribe();
        this.linesSubscription?.unsubscribe();
        if (this.selectedLiftSubscription) {
            this.selectedLiftSubscription.unsubscribe();
        }

        if (this.deleteTraySubscription) {
            this.deleteTraySubscription.unsubscribe();
        }

        if (this.deleteLiftSubscription) {
            this.deleteLiftSubscription.unsubscribe();
        }

        if (this.changeVisibilitySubscription) {
            this.changeVisibilitySubscription.unsubscribe();
        }
    }

    getLift(id: number) {
        this.store.dispatch(new LiftActions.LoadLiftAction(id, this.getToolFilters()));
    }

    private getToolFilters(): LiftToolFilters {
        const value = this.filterForm.value;
        return {
            toolUID: value.toolUID?.trim() || undefined,
            toolName: value.toolName?.trim() || undefined,
            lineId: value.line?.id || undefined
        };
    }

    toggleFilters() {
        this.activePanel = this.activePanel === 'filters' ? null : 'filters';
    }

    toggleAddTray() {
        this.activePanel = this.activePanel === 'add' ? null : 'add';
    }

    onEditClick() {
        if (this.activePanel === 'edit') {
            this.onCancelEdit();
            return;
        }
        this.liftForm.reset({name: this.selectedLift.name});
        this.activePanel = 'edit';
    }

    onCancelEdit() {
        this.liftForm.reset({name: this.selectedLift.name});
        this.activePanel = null;
    }

    onSaveClick() {
        if (this.liftForm.invalid) {
            this.liftForm.get('name').markAsTouched();
            return;
        }
        this.store.dispatch(new LiftActions.RenameLiftAction(this.selectedLift, this.liftForm.get('name').value))
    }

    onDelete() {
        this.confirmRemoveLift.open();
    }

    onAddTray() {
        if (!this.trayForm.get('slots').value || !this.trayForm.get('name').value) {
            this.trayForm.get('slots').markAsTouched();
            this.trayForm.get('name').markAsTouched();
            return;
        }

        this.store.dispatch(new LiftActions.AddTrayAction(this.selectedLift,
            parseInt(this.trayPositions.value, 0),
            parseInt(this.trayName.value, 0),
            this.forService.value ? TrayType.Service : TrayType.Normal));
        this.trayForm.reset();
    }

    onEditTray(tray: Tray) {
        this.router.navigate([`./trays/${tray.id}`], {relativeTo: this.route, queryParamsHandling: 'preserve'});
    }

    onTrayContextMenu(event: MouseEvent, tray: Tray) {
        event.preventDefault();
        this.trayContextMenuItems = [{
            label: this.translate.instant('generic.buttons.openNewTab'),
            icon: 'fa fa-external-link',
            command: () => {
                const url = this.router.serializeUrl(this.router.createUrlTree([`./trays/${tray.id}`], {
                    relativeTo: this.route,
                    queryParams: this.route.snapshot.queryParams
                }));
                localStorage.setItem('openedInNewTab', 'true');
                window.open(url, '_blank', 'noopener');
            }
        }];
        this.trayContextMenu.show(event);
    }

    onDeleteTray(tray: Tray) {
        this.toBeDeletedTray = tray;
        this.confirmRemoveTray.open();
    }

    onConfirmRemoveTray() {
        this.store.dispatch(new LiftActions.DeleteTrayAction(this.selectedLift, this.toBeDeletedTray));

        this.deleteTraySubscription = this.deletedTray$.pipe(filter(deleted => null !== deleted))
            .subscribe(deleted => {
                this.confirmRemoveTray.close();
            });
    }

    onConfirmRemoveLift() {
        this.store.dispatch(new LiftActions.DeleteLiftAction(this.selectedLift));

        this.deleteLiftSubscription = this.deletedLift$.pipe(filter(deleted => null !== deleted))
            .subscribe(deleted => {
                this.confirmRemoveLift.close();
            });
    }

    onConfirmChangeVisibilityTray() {
        const newTray = {...this.toChangeVisibilityTray, disabled: !this.toChangeVisibilityTray.disabled} as Tray;
        this.store.dispatch(new LiftActions.ToggleTrayStateAction(this.selectedLift, newTray));

        this.changeVisibilitySubscription = this.selectedLift$.pipe(filter(deleted => null !== deleted))
            .subscribe(deleted => {
                this.confirmChangeVisibilityTray?.close();
            });
    }

    onToggleTrayVisibility(tray: Tray) {
        this.toChangeVisibilityTray = tray;
        this.confirmChangeVisibilityTray.open()
    }

    onSelectSlot(slot: Slot) {
        this.store.dispatch(new LiftActions.LoadAllToolsBySlotAction(slot));
        this.modal.open();
    }

}
